import {
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  Control,
  Controller,
  FieldPath,
  FieldPathValue,
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IconSelectOption, LinkedSelectFieldType, SelectFieldType } from '@/type';
import { buildFormFieldLabel, isArrayEmpty } from '@/util';
import { StringSchema } from 'yup';

interface LinkedSelectProps<TFieldValues extends FieldValues = FieldValues> {
  control: Control<TFieldValues>;
  disabled: boolean;
  linkedSelectField: LinkedSelectFieldType;
}

const LinkedSelectController = <TFieldValues extends FieldValues = FieldValues>({
  control,
  disabled,
  linkedSelectField,
}: LinkedSelectProps<TFieldValues>) => {
  const { t } = useTranslation();
  const { setValue, clearErrors } = useFormContext<TFieldValues>();

  const primarySelectLabelId = `linked-select-primary-${linkedSelectField.primarySelect.name}`;
  const dependentSelectLabelId = `linked-select-dependent-${linkedSelectField.dependentSelect.name}`;

  const [dependentOptions, setDependentOptions] = useState<IconSelectOption[]>(
    linkedSelectField.dependentSelect.options || [],
  );

  useEffect(() => {
    setDependentOptions(linkedSelectField?.dependentSelect?.options);
  }, [linkedSelectField?.dependentSelect?.options]);

  const handlePrimarySelectChange = async (value: unknown) => {
    const options = await linkedSelectField.fetchDependentOptions(value);
    setValue(
      linkedSelectField.dependentSelect.name as FieldPath<TFieldValues>,
      undefined as FieldPathValue<TFieldValues, never>,
      { shouldValidate: true },
    );
    clearErrors(linkedSelectField.dependentSelect.name as FieldPath<TFieldValues>);
    setDependentOptions(options);
  };

  const getDefaultValues = (
    selectField: SelectFieldType,
  ): string | string[] | number | number[] => {
    if (selectField.defaultValue) {
      return selectField.defaultValue;
    }

    if (selectField.defaultValues) {
      return selectField.defaultValues;
    }

    return '';
  };

  const buildPrimarySelectLabel = () =>
    buildFormFieldLabel(
      t(linkedSelectField.primarySelect.label),
      linkedSelectField.primarySelect.validation as StringSchema,
    );
  const buildDependentSelectLabel = () =>
    buildFormFieldLabel(
      t(linkedSelectField.dependentSelect.label),
      linkedSelectField.dependentSelect.validation as StringSchema,
    );

  return (
    <>
      <Grid item xs={linkedSelectField.primarySelect?.xs ?? 12}>
        <Controller
          name={linkedSelectField.primarySelect.name as any}
          control={control}
          render={({ field: { onChange, value, ref }, fieldState }) => (
            <FormControl
              fullWidth
              required={linkedSelectField.primarySelect.required}
              error={!!fieldState.error}
            >
              <InputLabel id={primarySelectLabelId}>
                {buildPrimarySelectLabel()}
              </InputLabel>
              <Select
                id={linkedSelectField.primarySelect.name}
                labelId={primarySelectLabelId}
                label={buildPrimarySelectLabel()}
                onChange={(e) => {
                  onChange(e.target.value);
                  handlePrimarySelectChange(e.target.value).then();
                  //TODO: át a packagebe
                }}
                value={value}
                disabled={disabled}
                inputRef={ref}
                MenuProps={{
                  PaperProps: {
                    className: 'custom-dropdown',
                  },
                }}
              >
                {linkedSelectField.primarySelect.options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
              {fieldState.error && (
                <FormHelperText>{t(fieldState.error.message || '')}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      </Grid>

      <Grid item xs={linkedSelectField.dependentSelect?.xs ?? 12}>
        <Controller
          name={linkedSelectField.dependentSelect.name as any}
          control={control}
          render={({ field: { onChange, value, ref }, fieldState }) => (
            <FormControl
              fullWidth
              required={linkedSelectField.dependentSelect.required}
              error={!!fieldState.error}
            >
              <InputLabel id={dependentSelectLabelId}>
                {buildDependentSelectLabel()}
              </InputLabel>
              <Select
                id={linkedSelectField.dependentSelect.name}
                labelId={dependentSelectLabelId}
                label={buildDependentSelectLabel()}
                onChange={(e) => {
                  const changedValue = e.target.value;
                  onChange(changedValue);
                  linkedSelectField.dependentSelect?.onChange?.(changedValue);
                }}
                value={value ?? ''}
                inputRef={ref}
                disabled={disabled}
                MenuProps={{
                  PaperProps: {
                    className: 'custom-dropdown',
                  },
                }}
              >
                {isArrayEmpty(dependentOptions) ? (
                  <MenuItem disabled value=''>
                    {t('common.no_available_data')}
                  </MenuItem>
                ) : (
                  dependentOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))
                )}
              </Select>
              {fieldState.error && (
                <FormHelperText>{t(fieldState.error.message || '')}</FormHelperText>
              )}
            </FormControl>
          )}
        />
      </Grid>
    </>
  );
};

export default LinkedSelectController;
